import { toast } from "sonner";
import {
  useCreateWingWeighing,
  useDeleteWingWeighing,
  useSignOffWingWeighing,
  useUnlockWingWeighing,
  useUpdateWingWeighing,
} from "@/api/hooks/use-wing-weighings";
import type { BoatId, WingConfig, WingWeighingId } from "@/types/identifiers";
import type { components } from "@/api/generated/schema";

type WingWeighing = components["schemas"]["WingWeighingResponse"];
type WingWeighingCreate = components["schemas"]["WingWeighingCreate"];
type WingWeighingUpdate = components["schemas"]["WingWeighingUpdate"];

export interface WingWeighingActions {
  /** Resolves with the created weighing; rejects on failure so callers can keep their UI open for retry. */
  handleCreate: (data: WingWeighingCreate) => Promise<WingWeighing>;
  handleUpdate: (
    id: WingWeighingId,
    data: WingWeighingUpdate,
  ) => Promise<void>;
  handleSignOff: (id: WingWeighingId) => void;
  /** Awaitable variant for save-then-sign-off chains; rejects on failure. */
  handleSignOffAsync: (id: WingWeighingId) => Promise<void>;
  handleUnlock: (id: WingWeighingId) => void;
  handleDelete: (id: WingWeighingId) => void;
  isCreating: boolean;
  isUpdating: boolean;
  isSigningOff: boolean;
  isUnlocking: boolean;
  isDeleting: boolean;
  weighings: WingWeighing[];
  signedOffCount: number;
  /** Active signed-off weighing for the canonical APW config (used by fleet equalization). */
  activeApw24: WingWeighing | undefined;
  byConfig: (config: WingConfig) => WingWeighing[];
}

export function useWingWeighingActions(
  boatId: BoatId,
  weighings: WingWeighing[] | undefined,
): WingWeighingActions {
  const create = useCreateWingWeighing(boatId);
  const update = useUpdateWingWeighing(boatId);
  const signOff = useSignOffWingWeighing(boatId);
  const unlock = useUnlockWingWeighing(boatId);
  const del = useDeleteWingWeighing(boatId);

  const list = weighings ?? [];
  const signedOff = list.filter((w) => w.state === "signed_off");
  const activeApw24 = signedOff.find((w) => w.config === "APW");

  const handleCreate = (data: WingWeighingCreate) =>
    create.mutateAsync(data).then(
      (created) => {
        toast.success("Wing weighing created");
        return created;
      },
      (err: Error) => {
        toast.error(err.message);
        throw err;
      },
    );

  const handleUpdate = (id: WingWeighingId, data: WingWeighingUpdate) =>
    update.mutateAsync({ weighingId: id, data }).then(
      () => {
        toast.success("Wing weighing updated");
      },
      (err: Error) => {
        toast.error(err.message);
        throw err;
      },
    );

  const handleSignOffAsync = (id: WingWeighingId) =>
    signOff.mutateAsync(id).then(
      () => {
        toast.success("Wing weighing signed off");
      },
      (err: Error) => {
        toast.error(err.message);
        throw err;
      },
    );

  const handleSignOff = (id: WingWeighingId) => {
    void handleSignOffAsync(id).catch(() => {});
  };

  const handleUnlock = (id: WingWeighingId) => {
    unlock.mutate(id, {
      onSuccess: () => toast.success("Wing weighing unlocked"),
      onError: (err) => toast.error(err.message),
    });
  };

  const handleDelete = (id: WingWeighingId) => {
    del.mutate(id, {
      onSuccess: () => toast.success("Wing weighing deleted"),
      onError: (err) => toast.error(err.message),
    });
  };

  return {
    handleCreate,
    handleUpdate,
    handleSignOff,
    handleSignOffAsync,
    handleUnlock,
    handleDelete,
    isCreating: create.isPending,
    isUpdating: update.isPending,
    isSigningOff: signOff.isPending,
    isUnlocking: unlock.isPending,
    isDeleting: del.isPending,
    weighings: list,
    signedOffCount: signedOff.length,
    activeApw24,
    byConfig: (config: WingConfig) => list.filter((w) => w.config === config),
  };
}
